import type { Brand, BrandCrowdSignals, BrandReaction, BrandSummary, Tag } from "@shared/types";

export type { Brand };

export interface ListResponse<T> {
  items: T[];
  total: number;
  page?: number;
  pageSize?: number;
}

export interface ApiOptions {
  signal?: AbortSignal;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    // Same-origin — explicit. Switch to "include" if BM ever needs to call
    // its API from a different subdomain.
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* ignore */ }
    const message = (body as { message?: string } | null)?.message ?? `Request failed: ${res.status}`;
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

async function upload<T>(path: string, body: BodyInit, contentType: string): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": contentType },
    body,
  });
  if (!res.ok) {
    let responseBody: unknown = null;
    try { responseBody = await res.json(); } catch { /* ignore */ }
    const message = (responseBody as { message?: string } | null)?.message ?? `Request failed: ${res.status}`;
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

export const api = {
  brands: {
    list: (params: Record<string, string | number | undefined> = {}, options: ApiOptions = {}) =>
      request<ListResponse<BrandSummary>>(`/api/brands${qs(params)}`, { signal: options.signal }),
    get: (slug: string, options: ApiOptions = {}) => request<{ brand: Brand }>(`/api/brands/${slug}`, { signal: options.signal }),
    save: (id: string, note?: string) =>
      request<{ saved: boolean }>(`/api/brands/${id}/save`, { method: "POST", body: JSON.stringify({ note }) }),
    unsave: (id: string) =>
      request<{ saved: boolean }>(`/api/brands/${id}/save`, { method: "DELETE" }),
    react: (id: string, reaction: BrandReaction, active: boolean) =>
      request<{ crowdSignals: BrandCrowdSignals }>(`/api/brands/${id}/reactions`, {
        method: "POST",
        body: JSON.stringify({ reaction, active }),
      }),
  },
  tags: {
    list: (type?: string, params: { occupied?: boolean } = {}, options: ApiOptions = {}) =>
      request<{ tags: Tag[] }>(`/api/tags${qs({ type, occupied: params.occupied ? 1 : undefined })}`, { signal: options.signal }),
    brands: (slug: string, params: Record<string, string | number | undefined> = {}, options: ApiOptions = {}) =>
      request<{ items: BrandSummary[]; total: number }>(`/api/tags/${slug}/brands${qs(params)}`, { signal: options.signal }),
  },
  categories: {
    list: (params: { occupied?: boolean } = {}, options: ApiOptions = {}) =>
      request<{ categories: Tag[] }>(`/api/categories${qs({ occupied: params.occupied ? 1 : undefined })}`, { signal: options.signal }),
  },
  discover: () => request<{ items: BrandSummary[] }>("/api/discover"),
  radar: {
    status: () => request<{
      publishedBrands: number;
      freshBrands7d: number;
      crowdVotes: number;
      latestBrand: { name: string; slug: string; publishedAt: string | null } | null;
      lastRadarActivityAt: string | null;
    }>("/api/radar/status"),
    trends: (limit = 8) => request<{
      items: Array<{
        slug: string;
        name: string;
        type: string;
        brandCount: number;
        avgDiscovery: number;
        avgMomentum: number;
        latestPublishedAt: string | null;
      }>;
    }>(`/api/radar/trends${qs({ limit })}`),
  },
  search: (q: string, options: ApiOptions = {}) =>
    request<{ items: BrandSummary[]; total: number; query: string; mode: string }>(
      `/api/search?q=${encodeURIComponent(q)}`,
      { signal: options.signal },
    ),
  me: {
    savedBrands: (options: ApiOptions = {}) =>
      request<{ items: Array<BrandSummary & { saved_at: string; note: string | null }> }>("/api/me/saved-brands", { signal: options.signal }),
    submissions: () => request<{ items: unknown[] }>("/api/me/submissions"),
    submit: (url: string, message?: string) =>
      request<{ submissionId: string; candidateId: string }>("/api/submissions", {
        method: "POST",
        body: JSON.stringify({ url, message }),
      }),
  },
  admin: {
    dashboard: () => request<{
      counts: {
        newCandidatesToday: number;
        publishedToday: number;
        reviewQueueSize: number;
        failedJobs: number;
        totalPublishedBrands: number;
        rejectedSearchLeads7d: number;
        suppressedDomains7d: number;
        sourcePenalties7d: number;
      };
      topSources: Array<{ id: string; name: string; quality_score: number; signals_this_week: number; results_this_week: number; accepted_this_week: number; rejected_this_week: number }>;
      crowdPulse: Array<{ id: string; name: string; slug: string; early: number; would_wear: number; mainstream: number; not_it: number; total_votes: number; net_signal: number }>;
      recentAutoPublished: Array<{
        id: string;
        name: string;
        slug: string;
        canonical_domain: string | null;
        country: string | null;
        region: string | null;
        published_at: string | null;
        discovery_score: number | null;
        mainstream_score: number | null;
        momentum_score: number | null;
        auto_publish_score: number | null;
        log_score: number | null;
        log_event: string;
        logged_at: string;
      }>;
      latestQualityReport: {
        message: string;
        created_at: string;
        publishedTotal: number;
        published24h: number;
        published7d: number;
        missingImages: number;
        missingImageBrands: Array<{ name: string; slug: string }>;
        weakImages: number;
        weakImageBrands: Array<{ name: string; slug: string; imageUrl: string | null }>;
        logoPlateImages: number;
        logoPlateImageBrands: Array<{ name: string; slug: string; imageUrl: string | null }>;
        reviewQueue: number;
        failedJobs: number;
        social: { queued: number; approved: number; failed: number };
        discovery: { scanRuns24h: number; failedScanRuns24h: number };
        warnings: string[];
      } | null;
      operationalMonitor: {
        status: "healthy" | "watch" | "attention";
        score: number;
        updatedAt: string | null;
        summary: string;
        checks: Array<{
          id: string;
          label: string;
          value: number;
          severity: "ok" | "warning" | "danger";
          action: string;
          href: string;
        }>;
        latestIncident: { event_type: string; message: string; created_at: string } | null;
      };
      recentLogs: Array<{ event_type: string; message: string; created_at: string }>;
      recentRejectedLeads: Array<{ source_id: string | null; source_name: string | null; message: string; title: string | null; reason: string | null; created_at: string }>;
      recentSuppressedDomains: Array<{ source_id: string | null; source_name: string | null; message: string; title: string | null; url: string | null; created_at: string }>;
      recentSourcePenalties: Array<{ entity_type: string | null; entity_id: string | null; message: string; sourceName: string | null; from: number | null; to: number | null; sourceCount: number; created_at: string }>;
    }>("/api/admin/dashboard"),
    discoveryRun: () =>
      request<{ queued: boolean; cooldownUntil?: string; message?: string }>("/api/admin/discovery/run", { method: "POST" }),
    launchQualityReport: () =>
      request<{ ok: boolean }>("/api/admin/launch-quality-report", { method: "POST" }),
    repairWeakImages: (limit = 24) =>
      request<{ checked: number; repaired: number; skipped: number; items: Array<{ id: string; name: string; from: string | null; to: string }> }>(
        "/api/admin/image-quality/repair",
        { method: "POST", body: JSON.stringify({ limit }) },
      ),

    socialPosts: (status: "queued" | "approved" | "posted" | "skipped" | "failed" | "all" = "queued") =>
      request<{ items: Array<{
        id: string;
        brand_id: string;
        brand_name: string;
        brand_slug: string;
        instagram_handle: string | null;
        platform: string;
        format: string;
        status: string;
        score: number;
        caption: string;
        image_url: string | null;
        weak_image: boolean;
        reason_json: string | null;
        instagram_container_id: string | null;
        instagram_media_id: string | null;
        publish_error: string | null;
        publish_attempted_at: string | null;
        poster_r2_key: string | null;
        poster_url: string | null;
        poster_generated_at: string | null;
        created_at: string;
        updated_at: string;
        posted_at: string | null;
      }> }>(`/api/admin/social-posts${qs({ status })}`),
    socialSummary: () =>
      request<{
        total: number;
        counts: Record<"queued" | "approved" | "posted" | "skipped" | "failed", number>;
        weakImages: number;
        readyToPost: number;
        needsPoster: number;
      }>("/api/admin/social-posts/summary"),
    socialPublishStatus: () =>
      request<{
        ready: boolean;
        hasAccessToken: boolean;
        hasIgUserId: boolean;
        graphVersion: string;
        autoPublishMode: "approved" | "queued";
        autoPublishLimit: number;
        autoApproveSafeLimit: number;
        directPublishEnabled: boolean;
        missing: string[];
      }>("/api/admin/social-posts/publish-status"),
    socialTestInstagram: () =>
      request<{ ok: boolean; message: string; igUserId?: string; username?: string }>("/api/admin/social-posts/test-instagram", {
        method: "POST",
      }),
    socialGenerate: (limit = 8) =>
      request<{ created: number; skipped: number; imageRepair?: { checked: number; repaired: number; skipped: number } }>("/api/admin/social-posts/generate", {
        method: "POST",
        body: JSON.stringify({ limit }),
      }),
    socialApproveSafe: (limit = 3) =>
      request<{
        approved: number;
        skippedWeakImages: number;
        skippedMissingPoster: number;
        items: Array<{ id: string; brandId: string; name: string; score: number }>;
      }>(
        "/api/admin/social-posts/approve-safe",
        { method: "POST", body: JSON.stringify({ limit }) },
      ),
    socialCleanReadiness: () =>
      request<{ movedToQueued: number; items: Array<{ id: string; brandId: string; name: string; score: number }> }>(
        "/api/admin/social-posts/clean-readiness",
        { method: "POST" },
      ),
    socialSetStatus: (id: string, status: "queued" | "approved" | "posted" | "skipped" | "failed") =>
      request<{ ok: boolean }>(`/api/admin/social-posts/${id}/status`, {
        method: "POST",
        body: JSON.stringify({ status }),
      }),
    socialBulkStatus: (ids: string[], status: "queued" | "approved" | "posted" | "skipped" | "failed") =>
      request<{ updated: number }>("/api/admin/social-posts/bulk-status", {
        method: "POST",
        body: JSON.stringify({ ids, status }),
      }),
    socialPublish: (id: string) =>
      request<{ ok: boolean; mediaId?: string; message?: string }>(`/api/admin/social-posts/${id}/publish`, { method: "POST" }),
    socialStorePoster: (id: string, blob: Blob) =>
      upload<{ ok: boolean; posterUrl: string; posterR2Key: string; message?: string }>(
        `/api/admin/social-posts/${id}/poster`,
        blob,
        "image/png",
      ),
    socialRepairImage: (id: string) =>
      request<{ repaired: boolean; skipped: boolean; message: string; imageUrl?: string }>(
        `/api/admin/social-posts/${id}/repair-image`,
        { method: "POST" },
      ),
    socialRepairImages: (limit = 24) =>
      request<{ checked: number; repaired: number; skipped: number; items: Array<{ id: string; brandId: string; name: string; imageUrl: string }> }>(
        "/api/admin/social-posts/repair-images",
        { method: "POST", body: JSON.stringify({ limit }) },
      ),

    candidates: (status = "review") =>
      request<{ items: Array<{ id: string; suggested_name: string | null; input_url: string; status: string; created_at: string }> }>(
        `/api/admin/candidates${qs({ status })}`
      ),
    candidate: (id: string) =>
      request<{ candidate: Record<string, unknown>; signals: unknown[] }>(`/api/admin/candidates/${id}`),
    approve: (id: string) =>
      request<{ ok: boolean }>(`/api/admin/candidates/${id}/approve`, { method: "POST" }),
    reject: (id: string, reason?: string) =>
      request<{ ok: boolean }>(`/api/admin/candidates/${id}/reject`, { method: "POST", body: JSON.stringify({ reason }) }),
    reprocess: (id: string) =>
      request<{ queued: boolean }>(`/api/admin/candidates/${id}/reprocess`, { method: "POST" }),

    brandsList: (params: { status?: string; q?: string } = {}) =>
      request<{ items: Array<{ id: string; slug: string; name: string; canonical_domain: string | null; country: string | null; status: string; published_at: string | null; created_at: string }> }>(
        `/api/admin/brands${qs(params)}`
      ),
    brandGet: (id: string) =>
      request<{ brand: Brand; scoreEvidence: { evidence_json: string | null; scoring_version: string | null; updated_at: string } | null }>(
        `/api/admin/brands/${id}`
      ),
    brandCreate: (payload: Record<string, unknown>) =>
      request<{ id: string; slug: string }>("/api/admin/brands", { method: "POST", body: JSON.stringify(payload) }),
    brandUpdate: (id: string, payload: Record<string, unknown>) =>
      request<{ ok: boolean }>(`/api/admin/brands/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
    brandDelete: (id: string) =>
      request<{ ok: boolean }>(`/api/admin/brands/${id}`, { method: "DELETE" }),
    brandSetTags: (id: string, tagIds: string[], confidence = 100) =>
      request<{ ok: boolean }>(`/api/admin/brands/${id}/tags`, {
        method: "PUT", body: JSON.stringify({ tagIds, confidence }),
      }),
    brandMerge: (sourceId: string, targetBrandId: string) =>
      request<{ ok: boolean }>(`/api/admin/brands/${sourceId}/merge`, {
        method: "POST", body: JSON.stringify({ targetBrandId }),
      }),

    tagsList: (type?: string) =>
      request<{ items: Array<{ id: string; slug: string; name: string; type: string; description: string | null; parent_tag_id: string | null }> }>(
        `/api/admin/tags${qs({ type })}`
      ),
    tagCreate: (payload: { name: string; type: string; description?: string; parentTagId?: string }) =>
      request<{ id: string; slug: string }>("/api/admin/tags", { method: "POST", body: JSON.stringify(payload) }),
    tagUpdate: (id: string, payload: Record<string, unknown>) =>
      request<{ ok: boolean }>(`/api/admin/tags/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
    tagDelete: (id: string) =>
      request<{ ok: boolean }>(`/api/admin/tags/${id}`, { method: "DELETE" }),

    sourcesList: () =>
      request<{ items: Array<{ id: string; name: string; url: string; source_type: string; scan_frequency: string; status: string; quality_score: number; last_scanned_at: string | null; search_query: string | null; search_region: string | null; search_last_run_at: string | null; search_results_total: number | null }> }>(
        "/api/admin/sources"
      ),
    sourceCreate: (payload: { name: string; url?: string; sourceType: string; scanFrequency?: string; qualityScore?: number; query?: string; region?: string }) =>
      request<{ id: string }>("/api/admin/sources", { method: "POST", body: JSON.stringify(payload) }),
    sourceUpdate: (id: string, payload: Record<string, unknown>) =>
      request<{ ok: boolean }>(`/api/admin/sources/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
    sourceScan: (id: string) =>
      request<{ queued: boolean }>(`/api/admin/sources/${id}/scan`, { method: "POST" }),
    sourceDryRun: (id: string, count = 10) =>
      request<{ query: string; region: string | null; provider: string; count: number; wouldEnqueue: number; items: Array<{ url: string; filteredUrl: string | null; title: string; snippet: string | null; decision: { verdict: string; confidence: number; reason: string; suggestedName?: string | null }; wouldEnqueue: boolean }> }>(
        `/api/admin/sources/${id}/dry-run`, { method: "POST", body: JSON.stringify({ count }) }
      ),
    sourceRuns: (id: string) =>
      request<{ items: Array<{ id: string; status: string; results_total: number | null; candidates_found: number; candidates_new: number; candidates_rejected: number | null; started_at: string; finished_at: string | null; error_message: string | null }> }>(
        `/api/admin/sources/${id}/runs`
      ),

    importBrands: (rows: Array<Record<string, string>>) =>
      request<{ created: number; skipped: number; skippedDetails: Array<{ row: Record<string, string>; reason: string }> }>(
        "/api/admin/import/brands", { method: "POST", body: JSON.stringify({ rows }) }
      ),

    qualitySamples: (status: "pending" | "approved" | "corrected" = "pending") =>
      request<{ items: Array<{ id: string; brand_id: string; created_at: string; note: string | null; name: string; slug: string }> }>(
        `/api/admin/quality-samples${qs({ status })}`
      ),
    qualitySampleApprove: (id: string) =>
      request<{ ok: boolean }>(`/api/admin/quality-samples/${id}/approve`, { method: "POST" }),
    qualitySampleCorrect: (id: string, note: string) =>
      request<{ ok: boolean }>(`/api/admin/quality-samples/${id}/correct`, { method: "POST", body: JSON.stringify({ note }) }),

    reports: (status: "open" | "resolved" | "dismissed" = "open") =>
      request<{ items: Array<{ id: string; brand_id: string; reason: string; details: string | null; status: string; created_at: string; resolved_at: string | null; brand_name: string; brand_slug: string; reporter_email: string | null }> }>(
        `/api/admin/reports${qs({ status })}`
      ),
    reportResolve: (id: string, note?: string) =>
      request<{ ok: boolean }>(`/api/admin/reports/${id}/resolve`, { method: "POST", body: JSON.stringify({ note }) }),
    reportDismiss: (id: string) =>
      request<{ ok: boolean }>(`/api/admin/reports/${id}/dismiss`, { method: "POST" }),
  },
};

function qs(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== "");
  if (entries.length === 0) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of entries) sp.set(k, String(v));
  return `?${sp.toString()}`;
}
